import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Briefcase, CheckSquare, FileText, Eye, MousePointerClick, Globe, DollarSign, CreditCard, Megaphone, Download, TrendingUp, Home } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { downloadCsv } from "../../lib/csv";
import { Switch } from "../../components/ui/switch";
import { getShowHomeStats, setShowHomeStats } from "../../lib/settings";

const Kpi = ({ icon: Icon, label, value, testid, accent = "brand" }) => (
  <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-5" data-testid={testid}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent === "green" ? "bg-housing/15" : "bg-brand-soft"}`}>
      <Icon className={`w-5 h-5 ${accent === "green" ? "text-housing" : "text-brand"}`} />
    </div>
    <div className="font-heading text-3xl font-bold text-stone-text">{value}</div>
    <div className="text-sm text-stone-muted">{label}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 mb-6">
    <h2 className="font-heading text-lg font-bold text-stone-text mb-4">{title}</h2>
    {children}
  </div>
);

export default function AdminOverview() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobStats, setJobStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHome, setShowHome] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then(({ data }) => setStats(data)),
      api.get("/admin/users").then(({ data }) => setUsers(data || [])).catch(() => {}),
      api.get("/jobs/stats").then(({ data }) => setJobStats(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    getShowHomeStats().then(setShowHome);
  }, []);

  const activeJobsCount = jobStats?.active ?? stats?.jobs?.active ?? 0;
  const housingCount = jobStats?.with_housing ?? 0;

  const onToggleHome = async (value) => {
    setShowHome(value);
    const global = await setShowHomeStats(value);
    toast.success(global ? t("admin.overview.savedGlobal") : t("admin.overview.savedLocal"));
  };

  const regData = useMemo(() => Object.entries(stats?.registrations_by_day || {}).map(([date, count]) => ({ date: date.slice(5), count })), [stats]);
  const usersByPlan = useMemo(() => {
    const c = {};
    users.forEach((u) => { const p = u.subscription?.plan || "free"; c[p] = (c[p] || 0) + 1; });
    return Object.entries(c).map(([plan, count]) => ({ plan, count }));
  }, [users]);
  const revenueByPlan = useMemo(() => Object.entries(stats?.revenue?.by_plan || {}).map(([plan, amount]) => ({ plan, amount })), [stats]);
  const paidUsers = useMemo(() => users.filter((u) => ["basic", "advanced"].includes(u.subscription?.plan)), [users]);

  if (loading) return <div className="py-16 text-center text-stone-muted">{t("jobs.loading")}</div>;

  const money = (v) => `$${Number(v || 0).toLocaleString()}`;
  const rev = stats?.revenue || {};
  const PLAN_COLORS = { free: "#8A817C", trial: "#D95A41", basic: "#22C55E", advanced: "#2D2422" };

  const exportCsv = () => {
    const s = stats || {};
    const metrics = [
      { metric: "total_users", value: s.users?.total ?? 0 },
      { metric: "candidates", value: s.users?.candidates ?? 0 },
      { metric: "employers", value: s.users?.employers ?? 0 },
      { metric: "active_jobs", value: s.jobs?.active ?? 0 },
      { metric: "applications", value: s.applications ?? 0 },
      { metric: "job_requests", value: s.job_requests ?? 0 },
      { metric: "visits", value: s.traffic?.visits ?? 0 },
      { metric: "clicks", value: s.traffic?.clicks ?? 0 },
      { metric: "job_views", value: s.traffic?.job_views ?? 0 },
      { metric: "revenue_total", value: rev.total ?? 0 },
      { metric: "revenue_month", value: rev.month ?? 0 },
      { metric: "revenue_ads", value: rev.ads ?? 0 },
      { metric: "paid_subscriptions", value: rev.paid_count ?? 0 },
    ];
    downloadCsv(`restaurantejobs-metrics-${new Date().toISOString().slice(0, 10)}.csv`, metrics);
    const userRows = users.map((u) => ({
      name: u.name, email: u.email, role: u.role,
      plan: u.subscription?.plan || "free", status: u.subscription?.status || "",
      active: u.active, referrals: u.referral_count ?? 0, registered: u.created_at,
    }));
    downloadCsv(`restaurantejobs-users-${new Date().toISOString().slice(0, 10)}.csv`, userRows);
    toast.success(t("admin.overview.exportCsv") + " ✓");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-heading text-3xl font-bold text-stone-text">{t("admin.overview.title")}</h1>
        <button data-testid="admin-export-csv" onClick={exportCsv} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-stone-border font-semibold text-stone-sub hover:border-brand hover:text-brand transition-colors">
          <Download className="w-4 h-4" /> {t("admin.overview.exportCsv")}
        </button>
      </div>
      <AdminTabs />

      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-6 mb-6" data-testid="site-settings-card">
        <h2 className="font-heading text-lg font-bold text-stone-text mb-4">{t("admin.overview.siteSettings")}</h2>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-md">
            <div className="font-medium text-stone-text">{t("admin.overview.showHomeStats")}</div>
            <p className="text-sm text-stone-muted mt-0.5">{t("admin.overview.showHomeStatsHint")}</p>
          </div>
          <Switch data-testid="toggle-home-stats" checked={showHome} onCheckedChange={onToggleHome} />
        </div>
        <div className="mt-4 pt-4 border-t border-stone-border flex flex-wrap gap-6" data-testid="home-stats-preview">
          <div className="text-xs uppercase tracking-wide text-stone-muted w-full">{t("admin.overview.privateCounter")}</div>
          <div>
            <div className="font-heading text-2xl font-bold text-stone-text flex items-center gap-1.5"><Briefcase className="w-5 h-5 text-brand" /> {activeJobsCount}</div>
            <div className="text-sm text-stone-muted">{t("admin.overview.activeJobs")}</div>
          </div>
          <div>
            <div className="font-heading text-2xl font-bold text-housing flex items-center gap-1.5"><Home className="w-5 h-5" /> {housingCount}</div>
            <div className="text-sm text-stone-muted">{t("admin.overview.withHousing")}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Kpi icon={Users} label={t("admin.overview.totalUsers")} value={stats?.users?.total ?? 0} testid="kpi-users" />
        <Kpi icon={Users} label={t("admin.overview.candidates")} value={stats?.users?.candidates ?? 0} testid="kpi-candidates" />
        <Kpi icon={Users} label={t("admin.overview.employers")} value={stats?.users?.employers ?? 0} testid="kpi-employers" />
        <Kpi icon={Briefcase} label={t("admin.overview.activeJobs")} value={stats?.jobs?.active ?? 0} testid="kpi-activejobs" />
        <Kpi icon={CheckSquare} label={t("admin.overview.applications")} value={stats?.applications ?? 0} testid="kpi-applications" />
        <Kpi icon={FileText} label={t("admin.overview.jobRequests")} value={stats?.job_requests ?? 0} testid="kpi-jobrequests" />
      </div>

      <Section title={t("admin.overview.traffic")}>
        <div className="grid grid-cols-3 gap-4">
          <Kpi icon={Globe} label={t("admin.overview.visits")} value={stats?.traffic?.visits ?? 0} testid="kpi-visits" />
          <Kpi icon={MousePointerClick} label={t("admin.overview.clicks")} value={stats?.traffic?.clicks ?? 0} testid="kpi-clicks" />
          <Kpi icon={Eye} label={t("admin.overview.jobViews")} value={stats?.traffic?.job_views ?? 0} testid="kpi-jobviews" />
        </div>
      </Section>

      {stats?.can_view_revenue ? (
        <Section title={t("admin.overview.revenue")}>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <Kpi icon={DollarSign} label={t("admin.overview.revenueTotal")} value={money(rev.total)} testid="kpi-rev-total" accent="green" />
            <Kpi icon={DollarSign} label={t("admin.overview.today")} value={money(rev.today)} testid="kpi-rev-today" accent="green" />
            <Kpi icon={DollarSign} label={t("admin.overview.week")} value={money(rev.week)} testid="kpi-rev-week" accent="green" />
            <Kpi icon={DollarSign} label={t("admin.overview.month")} value={money(rev.month)} testid="kpi-rev-month" accent="green" />
            <Kpi icon={Megaphone} label={t("admin.overview.adsRevenue")} value={money(rev.ads)} testid="kpi-rev-ads" accent="green" />
            <Kpi icon={CreditCard} label={t("admin.overview.paidSubs")} value={rev.paid_count ?? 0} testid="kpi-paid-subs" accent="green" />
          </div>
          {revenueByPlan.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-stone-sub mb-2">{t("admin.overview.byPlan")}</h3>
              <div className="flex flex-wrap gap-3">
                {revenueByPlan.map((r) => (
                  <span key={r.plan} className="px-3 py-1.5 rounded-full bg-stone-bg text-stone-sub text-sm">{r.plan}: <b className="text-stone-text">{money(r.amount)}</b></span>
                ))}
              </div>
            </div>
          )}
        </Section>
      ) : (
        <Section title={t("admin.overview.revenue")}><p className="text-stone-muted text-sm">{t("admin.overview.noRevenue")}</p></Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title={t("admin.overview.registrations")}>
          <div style={{ width: "100%", height: 240 }} data-testid="chart-registrations">
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <BarChart data={regData}>
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#8A817C" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#8A817C" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#D95A41" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title={t("admin.overview.usersByPlan")}>
          <div style={{ width: "100%", height: 240 }} data-testid="chart-users-by-plan">
            <ResponsiveContainer minWidth={0} minHeight={0}>
              <BarChart data={usersByPlan}>
                <XAxis dataKey="plan" tick={{ fontSize: 12, fill: "#8A817C" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#8A817C" }} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {usersByPlan.map((e, i) => <Cell key={i} fill={PLAN_COLORS[e.plan] || "#D95A41"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <Section title={t("admin.overview.paidConversions")}>
        {paidUsers.length === 0 ? (
          <p className="text-stone-muted text-sm" data-testid="conversions-empty">{t("admin.overview.noConversions")}</p>
        ) : (
          <div className="space-y-2" data-testid="conversions-list">
            {paidUsers.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 py-2 border-b border-stone-border/60 last:border-0" data-testid="conversion-item">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-housing/15 flex items-center justify-center shrink-0"><TrendingUp className="w-4 h-4 text-housing" /></div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-stone-text truncate">{u.name}</div>
                    <div className="text-xs text-stone-muted truncate">{u.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${u.subscription?.plan === "advanced" ? "bg-stone-text/10 text-stone-text" : "bg-housing/15 text-housing"}`}>{u.subscription?.plan}</span>
                  <span className="text-xs text-stone-muted hidden sm:block">{t("admin.overview.convertedOn")} {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
